import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Clock, ChevronRight, Sparkles, Loader2, CheckCircle, Pencil, X, FileText, Trash2 } from "lucide-react";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { removeOutlineSection, removeOutlineTopic } from "@/lib/outlineMutations";

export interface CreationOutlineSection {
  title?: string;
  description?: string;
  topics?: string[];
  duration?: string;
}

export interface CreationOutline {
  title?: string;
  description?: string;
  sections?: CreationOutlineSection[];
  /** Some flows (e.g. generate-course-outline) return "modules" instead of "sections" */
  modules?: CreationOutlineSection[];
}

interface SkillNodeStub {
  id: string;
  title: string;
  description?: string | null;
}

interface EditState {
  nodeId: string;
  originalTopic: string;
  title: string;
  description: string;
}

interface CreationOutlineViewProps {
  outline: CreationOutline;
  /** When provided, topics are matched to skill-tree nodes and become clickable links. */
  courseId?: string;
  /** Passed to AI when generating node descriptions (with materials and outline context). */
  courseTitle?: string | null;
  /** Called after outline + skill tree are updated (e.g. delete topic/chapter). */
  onOutlineUpdated?: () => void;
}

/** Renders the course outline from the creation dialogue (sections or modules, topics, duration). */
const CreationOutlineView = ({ outline, courseId, courseTitle, onOutlineUpdated }: CreationOutlineViewProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const sections = outline.sections ?? outline.modules ?? [];

  const [nodes, setNodes] = useState<SkillNodeStub[]>([]);
  const [generatingSection, setGeneratingSection] = useState<number | null>(null);
  const [generatedSections, setGeneratedSections] = useState<Set<number>>(new Set());

  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [generatingDescriptions, setGeneratingDescriptions] = useState(false);
  const [descriptionProgress, setDescriptionProgress] = useState<{ done: number; total: number } | null>(null);
  const [outlineMutating, setOutlineMutating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "topic"; sectionIndex: number; topicIndex: number; label: string }
    | { kind: "section"; sectionIndex: number; label: string }
    | null
  >(null);

  const refreshNodesFromTree = useCallback(async () => {
    if (!courseId) return;
    const { data } = await invokeFunction("get-skill-tree", { body: { courseId } });
    if (data?.nodes) {
      setNodes(data.nodes.map((n: { id: string; title: string; description?: string | null }) => ({
        id: n.id,
        title: n.title,
        description: n.description ?? null,
      })));
    } else {
      setNodes([]);
    }
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;
    void refreshNodesFromTree();
    const onCourseContentUpdated = () => {
      void refreshNodesFromTree();
    };
    window.addEventListener("course-content-updated", onCourseContentUpdated);
    return () => window.removeEventListener("course-content-updated", onCourseContentUpdated);
  }, [courseId, refreshNodesFromTree]);

  const topicToNode = useMemo(() => {
    const map = new Map<string, SkillNodeStub>();
    for (const node of nodes) {
      map.set(node.title.toLowerCase().trim(), node);
    }
    return map;
  }, [nodes]);

  const handleTopicClick = (topic: string) => {
    const node = topicToNode.get(topic.toLowerCase().trim());
    if (node?.id && courseId) {
      navigate(`/project/${courseId}/node/${node.id}/lesson`, { state: { fromMaterials: true } });
    }
  };

  const startEdit = (node: SkillNodeStub, originalTopic: string) => {
    setEditing({
      nodeId: node.id,
      originalTopic,
      title: node.title,
      description: node.description ?? "",
    });
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing || !courseId) return;
    const newTitle = editing.title.trim();
    const newDescription = editing.description.trim() || null;
    if (!newTitle) return;

    setSaving(true);
    try {
      // 1. Update skill_nodes
      const { error } = await supabase
        .from("skill_nodes")
        .update({ title: newTitle, description: newDescription })
        .eq("id", editing.nodeId);
      if (error) throw error;

      // 2. If title changed, update the creation_outline topic string to stay in sync
      if (newTitle !== editing.originalTopic) {
        const { data: outlineRow } = await supabase
          .from("addie_outlines")
          .select("id, creation_outline")
          .eq("project_id", courseId)
          .maybeSingle();
        if (outlineRow?.creation_outline) {
          const co = outlineRow.creation_outline as any;
          const secs = co.sections ?? co.modules ?? [];
          let changed = false;
          for (const sec of secs) {
            if (Array.isArray(sec.topics)) {
              const idx = sec.topics.indexOf(editing.originalTopic);
              if (idx !== -1) {
                sec.topics[idx] = newTitle;
                changed = true;
                break;
              }
            }
          }
          if (changed) {
            await supabase
              .from("addie_outlines")
              .update({ creation_outline: co })
              .eq("id", outlineRow.id);
          }
        }
      }

      // 3. Update local state
      setNodes((prev) =>
        prev.map((n) =>
          n.id === editing.nodeId ? { ...n, title: newTitle, description: newDescription } : n
        )
      );

      setEditing(null);
      toast({ title: "Saved" });
    } catch {
      toast({ variant: "destructive", title: "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const nodesMissingDescription = useMemo(
    () => nodes.filter((n) => !n.description?.trim()),
    [nodes],
  );

  const generateAllDescriptions = async () => {
    if (!courseId || nodes.length === 0) return;
    // Fill empty first; if every node already has text, regenerate all (same control stays visible).
    const targets = nodesMissingDescription.length > 0 ? nodesMissingDescription : nodes;

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) {
      toast({ variant: "destructive", title: "Not authenticated" });
      return;
    }

    setGeneratingDescriptions(true);
    setDescriptionProgress({ done: 0, total: targets.length });

    let done = 0;
    for (const node of targets) {
      try {
        const { data } = await supabase.functions.invoke("course-assistant", {
          body: {
            mode: "describe",
            messages: [],
            context: {
              nodeId: node.id,
              nodeName: node.title,
              courseId: courseId ?? null,
              courseName: courseTitle?.trim() || null,
              pageType: "creation-outline",
            },
            _authToken: token,
          },
        });
        if (data?.description) {
          setNodes((prev) =>
            prev.map((n) => (n.id === node.id ? { ...n, description: data.description } : n))
          );
        }
      } catch {
        // continue on individual failures
      }
      done++;
      setDescriptionProgress({ done, total: targets.length });
    }

    setGeneratingDescriptions(false);
    setDescriptionProgress(null);
    const mode =
      nodesMissingDescription.length > 0 ? "filled missing" : "regenerated";
    toast({
      title: "Descriptions updated",
      description: `${done} node${done !== 1 ? "s" : ""} ${mode}.`,
    });
  };

  const refreshNodesAfterOutlineChange = async () => {
    await refreshNodesFromTree();
    onOutlineUpdated?.();
  };

  const executeOutlineDelete = async () => {
    if (!deleteTarget || !courseId) return;
    setOutlineMutating(true);
    try {
      const res =
        deleteTarget.kind === "topic"
          ? await removeOutlineTopic(courseId, deleteTarget.sectionIndex, deleteTarget.topicIndex)
          : await removeOutlineSection(courseId, deleteTarget.sectionIndex);
      if (res.error) {
        toast({ variant: "destructive", title: "Could not update outline", description: res.error });
      } else {
        toast({
          title: deleteTarget.kind === "topic" ? "Topic removed" : "Chapter removed",
          description: "Outline and skill tree have been updated.",
        });
        setDeleteTarget(null);
        await refreshNodesAfterOutlineChange();
      }
    } finally {
      setOutlineMutating(false);
    }
  };

  const handleGenerateAllNodes = async (sectionIndex: number) => {
    const section = sections[sectionIndex];
    if (!section.topics || !courseId) return;

    const nodeIds = section.topics
      .map((t) => topicToNode.get(t.toLowerCase().trim())?.id)
      .filter((id): id is string => !!id);

    if (nodeIds.length === 0) {
      toast({ variant: "destructive", title: "No matching nodes", description: "Topics in this section don't match any skill tree nodes yet." });
      return;
    }

    setGeneratingSection(sectionIndex);
    let succeeded = 0;
    let failed = 0;

    for (const nodeId of nodeIds) {
      try {
        const { error } = await invokeFunction("generate-node-content", {
          body: { skillNodeId: nodeId },
        });
        if (error) throw error;
        succeeded++;
      } catch {
        failed++;
      }
    }

    setGeneratingSection(null);
    setGeneratedSections((prev) => new Set(prev).add(sectionIndex));
    toast({
      title: "Generation complete",
      description: `${succeeded} node${succeeded !== 1 ? "s" : ""} generated${failed > 0 ? `, ${failed} failed` : ""}.`,
    });
  };

  return (
    <div className="space-y-6">
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && !outlineMutating && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === "section" ? "Delete this chapter?" : "Remove this topic?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "section" ? (
                <>
                  This removes &quot;{deleteTarget.label}&quot; from the course outline and deletes that chapter and all
                  skill nodes under it (lessons, slides, exams). This cannot be undone.
                </>
              ) : deleteTarget?.kind === "topic" ? (
                <>
                  Remove &quot;{deleteTarget.label}&quot; from the outline and delete its skill node and generated
                  content. This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={outlineMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={outlineMutating}
              onClick={(e) => {
                e.preventDefault();
                void executeOutlineDelete();
              }}
            >
              {outlineMutating ? "Working…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {courseId && (
        <div
          className="rounded-lg border border-border bg-muted/50 px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-tutorial="outline-generate-descriptions"
        >
          <p className="text-sm text-muted-foreground leading-relaxed">
            {nodes.length === 0 ? (
              <>
                <span className="font-medium text-foreground">Node descriptions</span> live on your skill tree. Open
                the <span className="font-medium text-foreground">Skill Tree</span> tab and create or generate the tree,
                then return here — we reload nodes when the course updates. If you already built the tree, use{" "}
                <span className="font-medium text-foreground">Check for nodes</span>.
              </>
            ) : nodesMissingDescription.length > 0 ? (
              <>
                Generate short, material-aware descriptions for nodes that don&apos;t have one yet, or refresh all after
                uploads.
              </>
            ) : (
              <>Every node has a description. Regenerate all to refresh with the latest materials and outline.</>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {nodes.length === 0 && (
              <Button type="button" variant="secondary" size="sm" onClick={() => void refreshNodesFromTree()}>
                Check for nodes
              </Button>
            )}
            {nodes.length > 0 && (
              <Button
                variant="default"
                size="sm"
                className="gap-1.5"
                disabled={generatingDescriptions}
                onClick={generateAllDescriptions}
                title={
                  nodesMissingDescription.length > 0
                    ? "Generate descriptions for nodes that do not have one yet"
                    : "Regenerate descriptions for every node using the latest course context"
                }
              >
                {generatingDescriptions ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {descriptionProgress
                      ? `Generating… ${descriptionProgress.done}/${descriptionProgress.total}`
                      : "Generating…"}
                  </>
                ) : (
                  <>
                    <FileText className="h-3.5 w-3.5" />
                    {nodesMissingDescription.length > 0 ? "Generate descriptions" : "Regenerate descriptions"}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
      {(outline.title || outline.description) && (
        <div className="space-y-2">
          {outline.title && <h3 className="text-2xl font-bold">{outline.title}</h3>}
          {outline.description && <p className="text-muted-foreground">{outline.description}</p>}
        </div>
      )}
      {sections.map((section, i) => {
        const sectionNodeIds = (section.topics ?? [])
          .map((t) => topicToNode.get(t.toLowerCase().trim())?.id)
          .filter(Boolean);
        const hasMatchingNodes = courseId && sectionNodeIds.length > 0;
        const isGenerating = generatingSection === i;
        const isDone = generatedSections.has(i);

        return (
          <Card key={i}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1.5 flex-1">
                  <CardTitle className="text-base">{section.title ?? `Section ${i + 1}`}</CardTitle>
                  {section.description && <CardDescription>{section.description}</CardDescription>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {courseId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={outlineMutating}
                      title="Delete chapter from outline and skill tree"
                      onClick={() =>
                        setDeleteTarget({
                          kind: "section",
                          sectionIndex: i,
                          label: section.title ?? `Chapter ${i + 1}`,
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {hasMatchingNodes && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-1.5"
                      disabled={isGenerating}
                      onClick={() => handleGenerateAllNodes(i)}
                      title={isDone ? "Regenerate nodes using latest materials" : "Generate lesson content for nodes in this section"}
                    >
                      {isGenerating ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" />Generating…</>
                      ) : isDone ? (
                        <><CheckCircle className="h-3.5 w-3.5 text-green-500" />Refresh with materials</>
                      ) : (
                        <><Sparkles className="h-3.5 w-3.5" />Generate All Nodes</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {section.topics && section.topics.length > 0 ? (
                <ul className="space-y-2">
                  {section.topics.map((topic, j) => {
                    const node = topicToNode.get(topic?.toLowerCase?.()?.trim() ?? "");
                    const isEditingThis = editing && node && editing.nodeId === node.id;

                    return (
                      <li key={j} className="group">
                        {isEditingThis ? (
                          // ── Inline edit form ──────────────────────────────
                          <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
                            <Input
                              ref={titleInputRef}
                              value={editing.title}
                              onChange={(e) => setEditing((prev) => prev ? { ...prev, title: e.target.value } : prev)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                              placeholder="Node title"
                              className="text-sm font-medium h-8"
                            />
                            <Textarea
                              value={editing.description}
                              onChange={(e) => setEditing((prev) => prev ? { ...prev, description: e.target.value } : prev)}
                              onKeyDown={(e) => { if (e.key === "Escape") cancelEdit(); }}
                              placeholder="Brief description of what learners will understand or be able to do…"
                              className="text-sm resize-none min-h-[64px]"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={saveEdit} disabled={saving} className="h-7 px-3 text-xs">
                                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving} className="h-7 px-3 text-xs">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // ── Normal display row ────────────────────────────
                          <div className="flex items-start gap-2 text-sm">
                            <span className="text-muted-foreground mt-0.5 shrink-0">•</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {node?.id ? (
                                  <button
                                    type="button"
                                    onClick={() => handleTopicClick(topic)}
                                    className="text-left hover:text-primary hover:underline underline-offset-2 transition-colors flex items-center gap-1 group/link"
                                  >
                                    <span className="font-medium">{node.title}</span>
                                    <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                  </button>
                                ) : (
                                  <span className="font-medium">{topic}</span>
                                )}
                                {node?.id && (
                                  <button
                                    type="button"
                                    onClick={() => startEdit(node, topic)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground p-0.5 rounded"
                                    title="Edit title and description"
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                )}
                                {courseId && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setDeleteTarget({
                                        kind: "topic",
                                        sectionIndex: i,
                                        topicIndex: j,
                                        label: String(topic ?? "").trim() || "Topic",
                                      })
                                    }
                                    disabled={outlineMutating}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive p-0.5 rounded ml-auto"
                                    title="Remove from outline and delete skill node"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                              {node?.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{node.description}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {section.duration && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                  <Clock className="h-4 w-4" />
                  <span>Duration: {section.duration}</span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default CreationOutlineView;
